import React, {memo} from 'react';
import parser from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Eyebrow from '@/components/atoms/Eyebrow';
import Icon from '@/components/atoms/Icon';
import ArrowButton from '@/components/atoms/ArrowButton';
import type {IconName} from '@/components/atoms/Icon';
import styles from './HomeDeliveryGuarantee.module.scss';

export interface DeliveryGuaranteeCard {
    title: string;
    subheading: string;
    badge: string;
    iconName?: string;
    featured?: boolean;
}

const iconMap: Record<string, IconName> = {
    bolt: 'Bolt',
    lock: 'Lock',
    security: 'Security',
    ownership: 'SupervisorAccount'
};

interface HomeDeliveryGuaranteeProps {
    eyebrow?: string;
    heading: string;
    subheading: string;
    cards: DeliveryGuaranteeCard[];
    ctaText?: string;
    ctaUrl?: string;
}

// Without a URL the CTA has no action (no page uses it that way today); left as built pending a decision.
const CtaButton = ({label, className}: {label: string; className: string}) => (
    <button type="button" className={`${styles.ctaButton} ${className}`}>
        {label}
        <span className={`MuiButton-endIcon ${styles.endIcon}`}>
            <Icon name="ArrowForward" />
        </span>
    </button>
);

const HomeDeliveryGuarantee = ({eyebrow, heading, subheading, cards, ctaText, ctaUrl}: HomeDeliveryGuaranteeProps) => {
    return (
        <div className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
            <Container>
                <div className={styles.header}>
                    {eyebrow && <Eyebrow text={eyebrow} />}
                    <h2 className={`font-headers ${styles.heading}`}>{heading ? parser(heading) : ''}</h2>
                    <p className={`font-regular-text ${styles.subheading}`}>{subheading ? parser(subheading) : ''}</p>
                </div>

                <div className={styles.grid}>
                    {cards.map((card, idx) => (
                        <div className={styles.gridItem} key={idx}>
                            <div className={styles.card}>
                                <div className={`delivery-card-featured-icon-box ${styles.iconBox}`}>
                                    {(() => {
                                        const name = card.iconName ? iconMap[card.iconName] : 'Bolt';
                                        return name ? <Icon name={name} className={styles.icon} /> : null;
                                    })()}
                                </div>
                                <p className={`font-headers delivery-card-featured-text ${styles.text} ${styles.title}`}>{card.title}</p>
                                <p className={`font-regular-text delivery-card-featured-text ${styles.text} ${styles.desc}`}>
                                    {card.subheading}
                                </p>
                                <div className={styles.badge}>
                                    <Icon name="Check" className={styles.badgeCheck} />
                                    <p className={`font-headers ${styles.badgeText}`}>{card.badge}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {ctaText && (
                    <div className={styles.ctaWrap}>
                        {ctaUrl ? (
                            <ArrowButton variant="outlinePrimary" href={ctaUrl} target="_blank">
                                {ctaText}
                            </ArrowButton>
                        ) : (
                            <CtaButton label={ctaText} className="moving-icon-button font-headers" />
                        )}
                    </div>
                )}
            </Container>
        </div>
    );
};

export default memo(HomeDeliveryGuarantee);
